/**
 * Detection Rules v2 — Custom per-type hooks + types.
 *
 * Backing routes: cmd/admin-bff/rules_custom.go.
 * Custom is the simplest type: operator-defined JSON Schemas validating
 * arbitrary detection bodies. Schemas are admin-CRUD, the rule's payload is
 * validated against the bound schema, and there's no engine, no importer,
 * and no dry-run for arbitrary custom rules.
 *
 * Query key convention:
 *   ["rules","custom","list",filters]
 *   ["rules","custom","detail",id]
 *   ["rules","custom","schemas"]
 *   ["rules","custom","schemas",slug]
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";
import type { LintMessage, Rule, RuleSeverity, RuleTLP, RuleVersion } from "./rules";

// ---------- Types ----------

export interface CustomMeta {
    ruleId: string;
    schemaSlug: string;
    payload: Record<string, unknown>;
}

export interface CustomSchema {
    slug: string;
    name: string;
    description: string;
    jsonSchema: Record<string, unknown>;
    ownerRole: string;
    createdBy?: string;
    createdAt: string;
}

export interface CustomListRow {
    id: string;
    slug: string;
    name: string;
    severity: string;
    status: string;
    currentVersion: number;
    publishedVersion?: number;
    tags: string[];
    imported: boolean;
    license?: string;
    updatedAt: string;
    schemaSlug: string;
    schemaName: string;
}

export interface CustomListFilters {
    q?: string;
    schemaSlug?: string;
    status?: string;
    imported?: boolean;
    limit?: number;
    offset?: number;
}

export interface CustomDetail {
    rule: Rule;
    customMeta: CustomMeta;
    currentVersion: RuleVersion;
    schema: CustomSchema | null;
}

// Re-export LintMessage so consumers don't need to import from `./rules`.
export type { LintMessage } from "./rules";

// ---------- Slug regex (shared with the wizard form validator) ----------

export const CUSTOM_SLUG_RE = /^[a-z][a-z0-9-]{1,40}$/;

// ---------- Queries ----------

function toQuery(filters: CustomListFilters): string {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
        if (v === undefined || v === null || v === "") continue;
        p.set(k, String(v));
    }
    return p.toString();
}

export function useCustomList(filters: CustomListFilters = {}) {
    return useQuery({
        queryKey: ["rules", "custom", "list", filters] as const,
        queryFn: () =>
            api.get<{ rules: CustomListRow[]; total: number }>(
                `/api/v2/rules/custom?${toQuery(filters)}`,
            ),
    });
}

/** Convenience for landing-card counts. */
export function useCustomRules(filters: CustomListFilters = {}) {
    return useCustomList(filters);
}

export function useCustomDetail(id: string | undefined) {
    return useQuery({
        queryKey: ["rules", "custom", "detail", id] as const,
        queryFn: () => api.get<CustomDetail>(`/api/v2/rules/custom/r/${id}`),
        enabled: !!id,
    });
}

export function useCustomSchemas() {
    return useQuery({
        queryKey: ["rules", "custom", "schemas"] as const,
        queryFn: () =>
            api
                .get<{ schemas: CustomSchema[] }>(`/api/v2/rules/custom/schemas`)
                .then((d) => d.schemas ?? []),
        staleTime: 30_000,
    });
}

export function useCustomSchema(slug: string | undefined) {
    return useQuery({
        queryKey: ["rules", "custom", "schemas", slug] as const,
        queryFn: () =>
            api
                .get<{ schema: CustomSchema }>(
                    `/api/v2/rules/custom/schemas/${encodeURIComponent(slug ?? "")}`,
                )
                .then((d) => d.schema),
        enabled: !!slug,
        staleTime: 30_000,
    });
}

/** How many rules currently bind to this schema. Cheap — limit=1 reads
 *  total off the list endpoint. */
export function useCountSchemaUsage(schemaSlug: string | undefined) {
    return useQuery({
        queryKey: ["rules", "custom", "schemas", "usage", schemaSlug] as const,
        queryFn: () =>
            api
                .get<{ total: number }>(
                    `/api/v2/rules/custom?schemaSlug=${encodeURIComponent(schemaSlug ?? "")}&limit=1`,
                )
                .then((d) => d.total ?? 0),
        enabled: !!schemaSlug,
        staleTime: 30_000,
    });
}

// ---------- Mutations: rules ----------

export interface CreateCustomInput {
    slug?: string;
    name: string;
    description: string;
    severity: RuleSeverity;
    tlp: RuleTLP;
    tags: string[];
    content: string;
    note?: string;
    meta: {
        schemaSlug: string;
        payload: Record<string, unknown>;
    };
}

export function useCreateCustom() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateCustomInput) =>
            api.post<{
                rule: Rule;
                customMeta: CustomMeta;
                lintMessages: LintMessage[];
            }>(`/api/v2/rules/custom`, input),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "custom", "list"] });
        },
    });
}

export interface PatchCustomMetaInput {
    schemaSlug?: string;
    payload?: Record<string, unknown>;
}

export function usePatchCustomMeta(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (patch: PatchCustomMetaInput) =>
            api.patch<{ customMeta: CustomMeta }>(
                `/api/v2/rules/custom/r/${id}`,
                patch,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "custom", "detail", id] });
            void qc.invalidateQueries({ queryKey: ["rules", "custom", "list"] });
        },
    });
}

export function usePutCustomContent(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: {
            content: string;
            payload?: Record<string, unknown>;
            note?: string;
        }) =>
            api.put<{ version: RuleVersion; lintMessages: LintMessage[] }>(
                `/api/v2/rules/custom/r/${id}/content`,
                input,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "custom", "detail", id] });
            void qc.invalidateQueries({ queryKey: ["rules", "custom", "list"] });
        },
    });
}

export function useLintCustom(id: string | undefined) {
    return useMutation({
        mutationFn: (input: { content: string; payload?: Record<string, unknown> }) =>
            api.post<{ lintMessages: LintMessage[] }>(
                `/api/v2/rules/custom/r/${id}/lint`,
                input,
            ),
    });
}

// ---------- Mutations: schemas ----------

export interface CreateCustomSchemaInput {
    slug: string;
    name: string;
    description: string;
    jsonSchema: Record<string, unknown>;
    ownerRole?: string;
}

export function useCreateCustomSchema() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateCustomSchemaInput) =>
            api.post<{ schema: CustomSchema }>(`/api/v2/rules/custom/schemas`, input),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "custom", "schemas"] });
        },
    });
}

export interface UpdateCustomSchemaInput {
    name?: string;
    description?: string;
    jsonSchema?: Record<string, unknown>;
    ownerRole?: string;
}

export function useUpdateCustomSchema(slug: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (patch: UpdateCustomSchemaInput) =>
            api.patch<{ schema: CustomSchema }>(
                `/api/v2/rules/custom/schemas/${encodeURIComponent(slug)}`,
                patch,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "custom", "schemas"] });
            void qc.invalidateQueries({ queryKey: ["rules", "custom", "schemas", slug] });
        },
    });
}

export interface DeleteSchemaConflict {
    error: string;
    slug: string;
    usedBy: number;
    errorCode: "schema_in_use";
}

export function useDeleteCustomSchema() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (slug: string) =>
            api.delete<unknown>(`/api/v2/rules/custom/schemas/${encodeURIComponent(slug)}`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "custom", "schemas"] });
            void qc.invalidateQueries({ queryKey: ["rules", "custom", "list"] });
        },
    });
}
